<template>
  <div class="scanner-page">
    <!-- Top Bar -->
    <header class="bol-header">
      <div class="header-left">
        <span class="header-logo">📷</span>
        <h1 class="header-title">Escáner</h1>
      </div>
      <div class="header-right">
        <router-link to="/boleteria" class="nav-link">🎫 Boletería</router-link>
        <button @click="logout" class="logout-btn">Salir</button>
      </div>
    </header>

    <div v-if="!token" class="auth-redirect">
      <p>Sesión no iniciada</p>
      <router-link :to="'/admin?redirect=/boleteria/scanner'" class="login-link">Iniciar sesión →</router-link>
    </div>

    <main v-else class="scanner-content">
      <!-- Scanner Area (hidden when result showing) -->
      <div v-if="!result" class="scanner-card">
        <div id="qr-reader" class="qr-reader"></div>
        <p v-if="!scannerReady" class="scanner-status">Cargando cámara...</p>
        <p v-if="scannerError" class="scanner-error">{{ scannerError }}</p>
        <button v-if="scannerReady && isNative" @click="toggleTorch" class="torch-btn">
          {{ torchOn ? '🔦 Apagar linterna' : '🔦 Linterna' }}
        </button>
      </div>

      <!-- Manual Entry (hidden when result showing) -->
      <div v-if="!result" class="manual-card">
        <div class="manual-row">
          <input
            v-model="manualUUID"
            class="form-input"
            placeholder="Ingresar UUID manualmente..."
            @keyup.enter="validateManual"
          >
          <button @click="validateManual" class="validate-btn" :disabled="!manualUUID || validating">
            Validar
          </button>
        </div>
      </div>

      <!-- Result Display (centered when showing) -->
      <div v-if="result" class="result-wrapper">
        <div class="result-card" :class="resultClass">
          <div class="result-icon">{{ resultIcon }}</div>
          <div class="result-message">{{ result.message }}</div>
          <div v-if="result.funcion" class="result-funcion">{{ result.funcion }}</div>
          <div v-if="result.contact" class="result-contact">
            {{ result.contact.first_name }} {{ result.contact.last_name }}
          </div>
          <div v-if="result.payment_method" class="result-detail">
            {{ paymentLabel(result.payment_method) }}
          </div>
          <div v-if="result.used_at" class="result-detail">
            Usado: {{ formatDate(result.used_at) }}
          </div>
          <button @click="dismissResult" class="dismiss-btn">Próximo →</button>
        </div>
      </div>

      <!-- Scan Counter -->
      <div class="counter-card">
        <span class="counter-value">{{ scanCount }}</span>
        <span class="counter-label">entradas escaneadas</span>
      </div>
    </main>
  </div>
</template>

<script>
import { authStore } from '@/store/auth.js'
import { Capacitor } from '@capacitor/core'
const API_BASE = '/api'

export default {
  name: 'BoleteriaScanner',
  data() {
    return {
      token: localStorage.getItem('babilonia_auth_token') || sessionStorage.getItem('admin_token') || null,
      scanner: null,
      scannerReady: false,
      scannerError: '',
      manualUUID: '',
      validating: false,
      result: null,
      scanCount: 0,
      lastScannedUUID: '',
      torchOn: false,
      isNative: Capacitor.isNativePlatform(),
    }
  },
  computed: {
    resultClass() {
      if (!this.result) return ''
      return {
        valid: 'result-valid',
        already_used: 'result-used',
        cancelled: 'result-cancelled',
        not_found: 'result-notfound',
      }[this.result.result] || 'result-notfound'
    },
    resultIcon() {
      if (!this.result) return ''
      return {
        valid: '✅',
        already_used: '⚠️',
        cancelled: '🚫',
        not_found: '❌',
      }[this.result.result] || '❓'
    }
  },
  async mounted() {
    if (!this.token) return
    await this.initScanner()
  },
  beforeUnmount() {
    this.stopScanner()
  },
  methods: {
    async toggleTorch() {
      try {
        this.torchOn = !this.torchOn
        // Get the active video track directly
        const videoEl = document.querySelector('#qr-reader video')
        if (videoEl && videoEl.srcObject) {
          const track = videoEl.srcObject.getVideoTracks()[0]
          if (track) {
            await track.applyConstraints({
              advanced: [{ torch: this.torchOn }]
            })
          }
        }
      } catch (err) {
        console.error('Torch error:', err)
        this.torchOn = false
      }
    },
    async initScanner() {
      // Load html5-qrcode library
      if (!window.Html5QrcodeScanner && !window.Html5Qrcode) {
        await new Promise((resolve) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js'
          script.onload = resolve
          script.onerror = () => {
            this.scannerError = 'Error cargando librería del escáner'
            resolve()
          }
          document.head.appendChild(script)
        })
      }

      if (!window.Html5Qrcode) {
        this.scannerError = 'Librería del escáner no disponible'
        return
      }

      try {
        this.scanner = new window.Html5Qrcode('qr-reader')
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => this.onScanSuccess(decodedText),
          () => { /* ignore errors during scanning */ }
        )
        this.scannerReady = true
      } catch (err) {
        this.scannerError = 'No se pudo acceder a la cámara. Verificá los permisos.'
        console.error('Scanner init error:', err)
      }
    },

    stopScanner() {
      if (this.scanner) {
        try {
          this.scanner.stop()
        } catch { /* ignore */ }
      }
    },

    async onScanSuccess(uuid) {
      // Debounce: don't scan same code twice in a row
      if (uuid === this.lastScannedUUID) return
      this.lastScannedUUID = uuid

      // Stop scanner (camera will be hidden by v-if when result shows)
      this.stopScanner()

      await this.validateTicket(uuid)
    },

    async validateManual() {
      if (!this.manualUUID) return
      await this.validateTicket(this.manualUUID.trim())
      this.manualUUID = ''
    },

    async validateTicket(uuid) {
      this.validating = true
      this.result = null

      try {
        const res = await authStore.apiFetch(`${API_BASE}/tickets/validate/${uuid}`, {
          method: 'POST',
        })

        const data = await res.json()
        this.result = data

        if (data.result === 'valid') {
          this.scanCount++
          // Vibrate on success if available
          if (navigator.vibrate) navigator.vibrate(200)
        } else if (data.result === 'already_used' || data.result === 'not_found') {
          if (navigator.vibrate) navigator.vibrate([100, 50, 100])
        }
      } catch {
        this.result = {
          result: 'not_found',
          message: 'Error de conexión',
        }
      }
      this.validating = false
    },

    dismissResult() {
      this.result = null
      this.lastScannedUUID = ''
      // Reinit scanner after DOM updates (v-if shows the qr-reader div again)
      this.$nextTick(async () => {
        await this.initScanner()
        if (this.torchOn) {
          // Reapply torch after scanner restarts
          setTimeout(async () => {
            const videoEl = document.querySelector('#qr-reader video')
            if (videoEl && videoEl.srcObject) {
              const track = videoEl.srcObject.getVideoTracks()[0]
              if (track) await track.applyConstraints({ advanced: [{ torch: true }] })
            }
          }, 500)
        }
      })
    },

    paymentLabel(method) {
      const labels = { cash: 'Efectivo', transfer: 'Transferencia', redtickets: 'RedTickets', invitation: 'Invitación', combo: 'Combo', '2x1': '2 por 1' }
      return labels[method] || method
    },

    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleString('es-UY', { timeStyle: 'short' })
    },

    logout() {
      sessionStorage.removeItem('admin_token')
      localStorage.removeItem('babilonia_auth_token')
      localStorage.removeItem('babilonia_auth_user')
      this.$router.push('/admin?redirect=/boleteria/scanner')
    }
  }
}
</script>

<style scoped>
.scanner-page {
  min-height: 100vh;
  background: #0a0a0a;
  color: #e0e0e0;
}

/* Header — same as boleteria */
.bol-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #111;
  border-bottom: 1px solid #222;
  flex-wrap: wrap;
  gap: 10px;
}
.header-left { display: flex; align-items: center; gap: 10px; }
.header-logo { font-size: 28px; }
.header-title { font-size: 20px; color: #c9a227; margin: 0; }
.header-right { display: flex; align-items: center; gap: 10px; }
.nav-link {
  color: #888; text-decoration: none; font-size: 13px;
  padding: 6px 10px; border-radius: 6px; transition: all 0.2s;
}
.nav-link:hover { color: #c9a227; background: #1a1a1a; }
.logout-btn {
  background: #333; color: #aaa; border: none; padding: 6px 14px;
  border-radius: 6px; cursor: pointer; font-size: 13px;
}
.auth-redirect { text-align: center; padding: 100px 20px; color: #666; }
.login-link { display: inline-block; margin-top: 15px; color: #c9a227; text-decoration: none; }

/* Content */
.scanner-content {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Scanner */
.scanner-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 16px;
  overflow: hidden;
}
.qr-reader {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}
.scanner-status {
  text-align: center;
  color: #666;
  padding: 40px 0;
}
.scanner-error {
  text-align: center;
  color: #e74c3c;
  padding: 10px 0;
  font-size: 14px;
}

/* Manual input */
.manual-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 14px;
}
.manual-row {
  display: flex;
  gap: 10px;
}
.form-input {
  flex: 1;
  padding: 12px 14px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}
.form-input:focus { border-color: #c9a227; }
.validate-btn {
  padding: 12px 20px;
  background: #c9a227;
  color: #000;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}
.validate-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Result wrapper — centers vertically when camera is hidden */
.result-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

/* Result */
.result-card {
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
  animation: fadeIn 0.3s;
  width: 100%;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.result-valid {
  background: #0a2a0a;
  border: 2px solid #2ecc71;
}
.result-used {
  background: #2a1a0a;
  border: 2px solid #f39c12;
}
.result-cancelled {
  background: #2a0a0a;
  border: 2px solid #e74c3c;
}
.result-notfound {
  background: #1a0a0a;
  border: 2px solid #e74c3c;
}
.result-icon { font-size: 48px; margin-bottom: 10px; }
.result-message { font-size: 20px; font-weight: bold; margin-bottom: 8px; }
.result-funcion { font-size: 16px; color: #c9a227; margin-bottom: 4px; }
.result-contact { font-size: 14px; color: #aaa; }
.result-detail { font-size: 13px; color: #666; margin-top: 4px; }
.dismiss-btn {
  margin-top: 16px;
  background: #222;
  border: 1px solid #333;
  color: #aaa;
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}
.dismiss-btn:hover { background: #333; color: #fff; }

/* Counter */
.counter-card {
  text-align: center;
  padding: 20px;
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
}
.counter-value {
  display: block;
  font-size: 48px;
  font-weight: bold;
  color: #c9a227;
}
.counter-label {
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
}

@media (max-width: 480px) {
  .scanner-content { padding: 12px 8px; }
  .result-message { font-size: 18px; }
}
</style>